import React, {
  createContext,
  MouseEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { Menu as MUIMenu, MenuProps as MUIMenuProps } from '@mui/material';
import * as R from 'ramda';

import {
  Icon,
  IconButton,
  IconButtonProps as IconButtonPropsType,
  IconProps as IconPropsType,
} from '@atom/mui';

import MenuItem from './MenuItem';

interface Context {
  open: boolean;
  close: (event: React.MouseEvent<any>) => void;
}

export const MenuContext = createContext<Context>({
  open: false,
  close: () => {},
});

type OriginType = {
  horizontal: number | 'center' | 'left' | 'right';
  vertical: number | 'top' | 'center' | 'bottom';
};

type CustomProps = {
  /**
   * optionally control menu open state
   */
  open?: boolean;
  /**
   * MUI icon to use for icon button
   * defaults to more_vert
   */
  icon?: ReactNode;
  /**
   * optional props to be passed to IconButton
   */
  IconButtonProps?: IconButtonPropsType;
  /**
   * optional props to be passed to Icon
   */
  IconProps?: IconPropsType;
  /**
   * denotes the given icon should be used standalone
   * and not wrapped with IconButton and Icon
   */
  noIconButton?: boolean;
  /**
   * turns off the minWidth of the menu popover
   */
  noMinWidth?: boolean;
  dataCyLabel?: string;
};

type AtomMUIProps = {
  disabled?: boolean;
  anchorOrigin?: OriginType;
  transformOrigin?: OriginType;
  classes?: any;
  children?: ReactNode;
  variant?: 'menu' | 'selectedMenu';
  id?: string;
};

export type MenuProps = CustomProps & AtomMUIProps;

const styles = {
  pointer: {
    cursor: 'pointer',
  },
};

export const Menu = (props: MenuProps) => {
  const [anchor, setAnchor] = useState<HTMLElement>(null);

  const {
    disabled = false,
    dataCyLabel = '',
    icon = 'more_vert',
    IconButtonProps = {},
    IconProps = {},
    noIconButton = false,
    noMinWidth = false,
    anchorOrigin = { horizontal: 'left', vertical: 'top' },
    transformOrigin = { horizontal: 'left', vertical: 'top' },
    ...data
  } = props;

  const slotProps = {
    paper: {
      sx: {
        minWidth: noMinWidth ? '0' : '15rem',
        width: 'auto',
      },
    },
  };

  const MUIProps: R.Omit<MUIMenuProps, 'open'> = R.omit(
    ['noMinWidth', 'open'],
    data,
  );

  const handleOpen = useCallback(
    (event: MouseEvent<any>) => {
      event.stopPropagation();

      if (!disabled) {
        setAnchor(event.currentTarget);
      }
    },
    [disabled],
  );

  const handleClose = useCallback((event: React.MouseEvent<any>) => {
    event.stopPropagation();
    setAnchor(null);
  }, []);

  return (
    <MenuContext.Provider value={{ open: !!anchor, close: handleClose }}>
      {noIconButton ? (
        <span onClick={handleOpen} style={disabled ? {} : styles.pointer}>
          {icon}
        </span>
      ) : (
        <IconButton
          disabled={disabled}
          onClick={handleOpen}
          data-cy={dataCyLabel}
          {...IconButtonProps}
        >
          <Icon {...IconProps}>{icon}</Icon>
        </IconButton>
      )}
      <MUIMenu
        open={props.open ?? !!anchor}
        anchorEl={anchor}
        onClose={handleClose}
        keepMounted
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={slotProps}
        {...MUIProps}
      />
    </MenuContext.Provider>
  );
};

Menu.MenuItem = MenuItem;

export default Menu;
